import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounceValue } from '../../shared/hooks/useDebounceValue';

/**
 * Состояние списка админки: страница, поиск, сортировка, фильтры + загрузка данных.
 *
 * Списки раньше тянули всю таблицу разом (articleforadmin отдавал все статьи)
 * и не умели ни искать, ни сортировать. Пагинация и поиск теперь серверные,
 * а хук держит их состояние в одном месте, чтобы страницы оставались короткими.
 *
 * @param fetcher (params) => Promise<{items,total,page,perPage,lastPage}>
 */
export const useAdminList = (fetcher, { perPage: initialPerPage = 20, sort = 'id', dir = 'desc', filters: initialFilters = {} } = {}) => {
    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState({ total: 0, lastPage: 1 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(initialPerPage);
    const [search, setSearch] = useState('');
    const [sorting, setSorting] = useState({ sort, dir });
    const [filters, setFilters] = useState(initialFilters);
    const [reloadToken, setReloadToken] = useState(0);

    const debouncedSearch = useDebounceValue(search, 350);

    // Загрузчик держим в ref, чтобы инлайновая стрелочная функция на стороне страницы
    // не перезапускала эффект на каждый рендер (это дало бы бесконечный цикл).
    //
    // Плата за это: смена самого загрузчика перезагрузку НЕ вызывает. Если у страницы
    // меняется источник данных (как у DictionaryPage при переходе между справочниками),
    // её нужно пересоздавать через key — тогда сбросится и состояние списка,
    // иначе на новый источник переедут поиск, номер страницы и фильтры от старого.
    const fetcherRef = useRef(fetcher);
    fetcherRef.current = fetcher;

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        const params = {
            page,
            perPage,
            sort: sorting.sort,
            dir: sorting.dir,
            ...filters,
        };
        if (debouncedSearch.trim()) params.search = debouncedSearch.trim();

        fetcherRef
            .current(params)
            .then((data) => {
                if (cancelled) return;
                setRows(data.items || []);
                setMeta({ total: data.total || 0, lastPage: data.lastPage || 1 });
            })
            .catch((err) => {
                if (cancelled) return;
                setRows([]);
                setError(err);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [page, perPage, debouncedSearch, sorting, filters, reloadToken]);

    const reload = useCallback(() => setReloadToken((t) => t + 1), []);

    // Любое изменение условий выборки возвращает на первую страницу: иначе после
    // поиска можно оказаться на несуществующей десятой странице. Сброс делается
    // прямо в сеттерах, а не отдельным эффектом, — иначе успевал уйти лишний
    // запрос со старым номером страницы и в таблице моргали чужие данные.
    const changeSearch = useCallback((value) => {
        setSearch(value);
        setPage(1);
    }, []);

    const changePerPage = useCallback((value) => {
        setPerPage(value);
        setPage(1);
    }, []);

    const toggleSort = useCallback((column) => {
        setSorting((current) =>
            current.sort === column
                ? { sort: column, dir: current.dir === 'asc' ? 'desc' : 'asc' }
                : { sort: column, dir: 'asc' }
        );
        setPage(1);
    }, []);

    const setFilter = useCallback((key, value) => {
        const empty = value === '' || value === null || value === undefined;

        setFilters((current) => {
            const unchanged = empty ? !(key in current) : current[key] === value;
            // Возврат того же объекта важен: иначе выставление уже действующего
            // фильтра меняло бы идентичность filters и запускало лишнюю загрузку.
            if (unchanged) return current;

            const next = { ...current };
            if (empty) {
                delete next[key];
            } else {
                next[key] = value;
            }
            return next;
        });
        setPage(1);
    }, []);

    return {
        rows,
        total: meta.total,
        lastPage: meta.lastPage,
        loading,
        error,
        page,
        setPage,
        perPage,
        setPerPage: changePerPage,
        search,
        setSearch: changeSearch,
        sort: sorting.sort,
        dir: sorting.dir,
        toggleSort,
        filters,
        setFilter,
        reload,
    };
};

export default useAdminList;
